import {
  blogCommentsResponseSchema, blogReactionsResponseSchema, blogMeResponseSchema,
  type BlogComment,
} from '@gadenin/contract';
import { z } from 'zod';
import type { ApiClient, ApiFailure, ApiResult } from '../api/BffApiClient';
import { createBffApiClient } from '../api/BffApiClient';

/**
 * ブログ参加 (コメント / helpful / 通報) の BFF クライアント。
 *
 * 呼び先はブログ専用の口 (`/bff/blog/*`)。日時はすべて **ISO 8601 文字列** (BE 契約)。
 * 公開 read は未認証でも成功する。認証が要る操作は 401 が返り、UI がサインイン誘導へ切り替える。
 */
export type BlogParticipationClient = {
  comments(articleId: string): Promise<ApiResult<BlogComment[]>>;
  reactions(articleId: string): Promise<ApiResult<{ helpful: number }>>;
  /** 自分の状態。未認証 (401) は「サインインしていない」として扱う */
  me(articleId: string): Promise<ApiResult<{ helpful: boolean; reportedCommentIds: string[] }>>;
  postComment(articleId: string, body: string): Promise<ApiResult<BlogComment>>;
  editComment(commentId: string, body: string): Promise<ApiResult<BlogComment>>;
  deleteComment(commentId: string): Promise<ApiResult<void>>;
  setHelpful(articleId: string, on: boolean): Promise<ApiResult<{ helpful: number }>>;
  reportComment(commentId: string, reason: string): Promise<ApiResult<void>>;
};

const commentResponseSchema = z.object({ comment: z.unknown() }).transform((value) => value.comment);

export function createBlogParticipationClient(deps: { api?: ApiClient; readCsrfToken?: () => string | null } = {}): BlogParticipationClient {
  const api = deps.api ?? createBffApiClient({
    basePath: '/bff/blog',
    readCsrfToken: deps.readCsrfToken ?? readCsrfCookie,
  });

  return {
    comments: async (articleId) => {
      const result = await api.request(`/articles/${articleId}/comments`, blogCommentsResponseSchema);
      return result.ok ? { ok: true, value: result.value.comments } : result;
    },
    reactions: (articleId) => api.request(`/articles/${articleId}/reactions`, blogReactionsResponseSchema),
    me: (articleId) => api.request(`/articles/${articleId}/me`, blogMeResponseSchema),
    postComment: async (articleId, body) => {
      const result = await api.request(`/articles/${articleId}/comments`, commentResponseSchema, {
        method: 'POST', body: { body },
      });
      return parseComment(result);
    },
    editComment: async (commentId, body) => {
      const result = await api.request(`/comments/${commentId}`, commentResponseSchema, {
        method: 'PATCH', body: { body },
      });
      return parseComment(result);
    },
    deleteComment: (commentId) => api.send(`/comments/${commentId}`, { method: 'DELETE' }),
    setHelpful: (articleId, on) => api.request(`/articles/${articleId}/reactions/helpful`, blogReactionsResponseSchema, {
      method: on ? 'PUT' : 'DELETE',
    }),
    // 初回 201 / 重複再送 200 — どちらも成功として扱う (BE 契約)
    reportComment: (commentId, reason) => api.send(`/comments/${commentId}/reports`, {
      method: 'POST', body: { reason },
    }),
  };
}

function parseComment(result: ApiResult<unknown>): ApiResult<BlogComment> {
  if (!result.ok) return result;
  const parsed = blogCommentsResponseSchema.shape.comments.element.safeParse(result.value);
  if (!parsed.success) return { ok: false, failure: { kind: 'invalidResponse' } satisfies ApiFailure };
  return { ok: true, value: parsed.data };
}

/** SPA (App.tsx) と同じ double-submit cookie 方式 */
export function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === '__Host-gdn_csrf') return rest.join('=') || null;
  }
  return null;
}
